import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "canvas",
    "input",
    "preview",
    "previewContainer",
    "placeholder",
    "controls",
    "backgroundInput",
    "clearBackgroundBtn",
    "clearInput",
  ]

  static values = {
    penColor: { type: String, default: "rgb(66, 133, 244)" },
    backgroundColor: { type: String, default: "rgb(255, 255, 255)" },
    // Opt-in (timesheet and custom report sketch fields): "Löschen" on a pad
    // left with no stroke and no background empties the input instead of saving
    // a blank white PNG, which would replace the real sketch and satisfy a
    // required field.
    clearRemovesSketch: { type: Boolean, default: false },
    // A saved sketch (custom reports: the field's attachment, served same-origin). It becomes a
    // layer of its own under the strokes, so undo/resize keep it and an uploaded background
    // image does not replace it.
    existingUrl: { type: String, default: "" },
  }

  connect() {
    this.backgroundImage = null
    this.savedSketch = null
    this.history = [] // Track all actions for unified undo

    // Don't initialize canvas here - it's hidden and has 0 dimensions
    // Canvas will be initialized when open() is called

    // Handle window resize
    this.boundResizeCanvas = this.handleResize.bind(this)
    window.addEventListener("resize", this.boundResizeCanvas)
  }

  disconnect() {
    if (this.signaturePad) {
      this.signaturePad.off()
    }
    if (this.boundResizeCanvas) {
      window.removeEventListener("resize", this.boundResizeCanvas)
    }
  }

  initializeCanvas() {
    const canvas = this.canvasTarget
    if (!canvas) return

    // Set canvas dimensions
    this.resizeCanvas()

    // Initialize SignaturePad
    this.signaturePad = new SignaturePad(canvas, {
      penColor: this.penColorValue,
      backgroundColor: this.backgroundColorValue,
    })

    // Save sketch data on stroke end and track in history
    this.signaturePad.addEventListener("endStroke", () => {
      // Push stroke action to history
      this.history.push({ type: "stroke" })
      this.saveSketch()
    })
  }

  resizeCanvas() {
    const canvas = this.canvasTarget
    const ratio = Math.max(window.devicePixelRatio || 1, 1)

    // Save current data before resizing
    const data = this.signaturePad?.toData()

    // Set canvas size
    canvas.width = canvas.offsetWidth * ratio
    canvas.height = canvas.offsetHeight * ratio

    // Reset transform and apply fresh scale (important: scale is cumulative!)
    const ctx = canvas.getContext("2d")
    ctx.setTransform(1, 0, 0, 1, 0, 0) // Reset to identity matrix
    ctx.scale(ratio, ratio)

    this.drawBackground()

    // Restore stroke data after resize (clear: false to preserve background)
    if (this.signaturePad && data && data.length > 0) {
      this.signaturePad.fromData(data, { clear: false })
    }
  }

  handleResize() {
    // Debounce resize handling
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.resizeCanvas()
    }, 100)
  }

  // The sketch to draw on: a drawing not saved yet, else the saved one. Loaded once, as its own
  // layer, and not part of the undo history. Until it has loaded the pad takes no strokes and
  // nothing is saved — a save before then would replace it with the strokes alone. If it never
  // loads, only "Löschen" can drop it.
  loadExistingSketch() {
    const source = this.inputTarget.value || this.existingUrlValue
    if (this.existingLoaded || !this.signaturePad || !source) return

    this.existingLoaded = true
    this.untouchedValue = this.inputTarget.value
    const img = new Image()
    this.loadingSketch = img
    this.signaturePad.off()
    img.onload = () => {
      if (this.loadingSketch !== img) return

      this.loadingSketch = null
      this.savedSketch = img
      this.signaturePad.on()
      this.redraw()
      // A background picked meanwhile was not saved yet.
      if (this.history.length > 0) this.persist()
    }
    img.src = source
  }

  // Redraws the layers under the current strokes.
  redraw() {
    const currentStrokes = this.signaturePad.toData()
    this.signaturePad.clear()
    this.drawBackground()
    this.signaturePad.fromData(currentStrokes, { clear: false })
  }

  // Stores what the pad shows. Nothing drawn over the saved sketch keeps it as it is; a pad
  // left with nothing at all is removed where clearRemovesSketch is set.
  persist() {
    if (!this.signaturePad.isEmpty() || this.backgroundImage) {
      this.saveSketch()
    } else if (this.savedSketch) {
      this.keepSavedSketch()
    } else if (this.clearRemovesSketchValue) {
      this.removeSketch()
    } else {
      this.saveSketch()
    }
  }

  keepSavedSketch() {
    this.inputTarget.value = this.untouchedValue
    this.setClearFlag(false)
    if (this.hasPreviewTarget) this.previewTarget.src = this.savedSketch.src
  }

  // Save sketch to hidden input
  saveSketch() {
    if (this.signaturePad && !this.loadingSketch) {
      // Composite background and drawing
      const dataUrl = this.getCompositeImage()
      this.inputTarget.value = dataUrl
      this.setClearFlag(false)
      this.updatePreview()
    }
  }

  // Get composite image with background and drawing
  getCompositeImage() {
    const canvas = this.canvasTarget
    const tempCanvas = document.createElement("canvas")
    tempCanvas.width = canvas.width
    tempCanvas.height = canvas.height
    const tempCtx = tempCanvas.getContext("2d")
    const ratio = Math.max(window.devicePixelRatio || 1, 1)

    // Fill with white background
    tempCtx.fillStyle = this.backgroundColorValue
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)

    // Background image and saved sketch
    tempCtx.scale(ratio, ratio)
    this.drawLayers(tempCtx, canvas.offsetWidth, canvas.offsetHeight)
    tempCtx.setTransform(1, 0, 0, 1, 0, 0)

    // Draw the sketch on top
    tempCtx.drawImage(canvas, 0, 0)

    return tempCanvas.toDataURL("image/png")
  }

  // Update preview thumbnail
  updatePreview() {
    if (this.hasPreviewTarget && this.inputTarget.value) {
      this.previewTarget.src = this.inputTarget.value
      this.previewTarget.classList.remove("hidden")
      if (this.hasPlaceholderTarget) {
        this.placeholderTarget.classList.add("hidden")
      }
    }
  }

  // Clear the canvas
  clear() {
    if (this.signaturePad) {
      // The saved sketch goes with the strokes, also one still loading or that failed to load;
      // an uploaded background image stays.
      this.savedSketch = null
      this.loadingSketch = null
      this.signaturePad.on()
      this.signaturePad.clear()
      this.drawBackground()

      if (this.clearRemovesSketchValue && this.signaturePad.isEmpty() && !this.backgroundImage) {
        this.removeSketch()
      } else {
        this.saveSketch()
      }
    }
  }

  // Nothing drawn: no value, and no stale preview to reopen.
  removeSketch() {
    this.inputTarget.value = ""
    this.setClearFlag(true)
    if (this.hasPreviewTarget) this.previewTarget.removeAttribute("src")
  }

  // Tells the server to drop the saved sketch (custom reports); a new stroke takes it back.
  setClearFlag(cleared) {
    if (this.hasClearInputTarget) this.clearInputTarget.value = cleared ? "1" : "0"
  }

  // Unified undo - handles both strokes and background changes
  undo() {
    if (!this.signaturePad || this.history.length === 0) return

    const lastAction = this.history.pop()

    if (lastAction.type === "stroke") {
      // Undo last stroke
      const data = this.signaturePad.toData()
      if (data.length > 0) {
        data.pop()
        this.signaturePad.clear()
        this.drawBackground()

        // Restore remaining strokes (clear: false to preserve background)
        this.signaturePad.fromData(data, { clear: false })
        this.persist()
      }
    } else if (lastAction.type === "background") {
      // Undo background change - restore previous background (or none)
      const currentStrokes = this.signaturePad.toData()

      this.backgroundImage = lastAction.previousBackground || null
      this.signaturePad.clear()
      this.drawBackground()

      if (this.backgroundImage) {
        // Show clear background button
        if (this.hasClearBackgroundBtnTarget) {
          this.clearBackgroundBtnTarget.classList.remove("hidden")
        }
      } else {
        // Hide clear background button
        if (this.hasClearBackgroundBtnTarget) {
          this.clearBackgroundBtnTarget.classList.add("hidden")
        }
      }

      // Restore strokes on top (clear: false to preserve background)
      this.signaturePad.fromData(currentStrokes, { clear: false })
      this.persist()

      // Reset file input
      if (this.hasBackgroundInputTarget) {
        this.backgroundInputTarget.value = ""
      }
    }
  }

  // Handle background image upload - preserves existing strokes
  uploadBackground(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        // Save current strokes before changing background
        const currentStrokes = this.signaturePad.toData()

        // Push background action to history with previous background state
        this.history.push({
          type: "background",
          previousBackground: this.backgroundImage,
        })

        // Set new background
        this.backgroundImage = img
        this.signaturePad.clear()
        this.drawBackground()

        // Restore strokes on top of new background (clear: false to preserve background)
        this.signaturePad.fromData(currentStrokes, { clear: false })

        // Show clear background button
        if (this.hasClearBackgroundBtnTarget) {
          this.clearBackgroundBtnTarget.classList.remove("hidden")
        }

        this.saveSketch()
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  // Draw the background image and the saved sketch on the canvas
  drawBackground() {
    if (!this.backgroundImage && !this.savedSketch) return

    const canvas = this.canvasTarget
    const ctx = canvas.getContext("2d")

    // Clear and fill with white
    ctx.fillStyle = this.backgroundColorValue
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight)

    this.drawLayers(ctx, canvas.offsetWidth, canvas.offsetHeight)
  }

  // The uploaded background, then the saved sketch over it. The saved sketch is a flat PNG on
  // white; multiplied, its white lets the background show through while its lines stay on top.
  drawLayers(ctx, width, height) {
    if (this.backgroundImage) this.drawFitted(ctx, this.backgroundImage, width, height)
    if (this.savedSketch) {
      ctx.save()
      ctx.globalCompositeOperation = "multiply"
      this.drawFitted(ctx, this.savedSketch, width, height)
      ctx.restore()
    }
  }

  // Scaled to fit and centered
  drawFitted(ctx, img, width, height) {
    const scale = Math.min(width / img.width, height / img.height)
    const x = (width - img.width * scale) / 2
    const y = (height - img.height * scale) / 2
    ctx.drawImage(img, x, y, img.width * scale, img.height * scale)
  }

  // Clear background image (also tracked in history for undo)
  clearBackground() {
    if (!this.backgroundImage) return

    // Save current strokes
    const currentStrokes = this.signaturePad.toData()

    // Push background action to history with previous background state
    this.history.push({
      type: "background",
      previousBackground: this.backgroundImage,
    })

    this.backgroundImage = null

    // Clear and redraw strokes without background (the saved sketch stays)
    if (this.signaturePad) {
      this.signaturePad.clear()
      this.drawBackground()
      this.signaturePad.fromData(currentStrokes, { clear: false })
    }

    // Hide clear background button
    if (this.hasClearBackgroundBtnTarget) {
      this.clearBackgroundBtnTarget.classList.add("hidden")
    }

    // Reset file input
    if (this.hasBackgroundInputTarget) {
      this.backgroundInputTarget.value = ""
    }

    this.persist()
  }

  // Open sketch editor (show canvas, hide preview)
  open() {
    // Hide preview and placeholder
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add("hidden")
    }
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }

    // Show the canvas controls
    if (this.hasControlsTarget) {
      this.controlsTarget.classList.remove("hidden")
    }

    // Wait for layout to be computed after showing the canvas
    requestAnimationFrame(() => {
      // Initialize canvas if not already done
      if (!this.signaturePad) {
        this.initializeCanvas()
      } else {
        // Ensure canvas is properly sized
        this.resizeCanvas()
      }

      this.loadExistingSketch()
    })
  }

  // Close/collapse sketch area
  close() {
    if (this.hasControlsTarget && this.inputTarget.value) {
      // Keep controls visible if there's a sketch
      this.updatePreview()
    }
  }
}
